import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { read } from '../../services/httpService';
import {
	apiGetWorkerBoardingsBriefSummary,
	IWorkerBoardingsBriefSummary,
	IWorkerBoardingBrief,
	IMonthlyWorkerNullCount,
	IProjectNumberBackEnd,
	apiGetAllProjectNumber,
	IWorkerNameBackEnd,
	apiGetAllWorkerNames,
} from '../../services/apiService';
import * as XLSX from 'xlsx';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts';
import { Download } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const COLORS = ['#FF0000', '#0088FE'];

export default function WorkerBoardingReport() {
	// dropdowns
	const [dropdownWOs, setDropdownWOs] = useState<IProjectNumberBackEnd[]>([]);
	const [dropdownWorkers, setDropdownWorkers] = useState<IWorkerNameBackEnd[]>(
		[]
	);
	// filters
	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [selectedWOs, setSelectedWOs] = useState<number[]>([]);
	const [selectedWorkerIds, setSelectedWorkerIds] = useState<number[]>([]);
	// summary
	const [summary, setSummary] = useState<IWorkerBoardingsBriefSummary | null>(
		null
	);
	const [loading, setLoading] = useState<boolean>(false);

	// carregamento inicial e busca padrão
	useEffect(() => {
		apiGetAllProjectNumber().then(setDropdownWOs);
		apiGetAllWorkerNames().then(setDropdownWorkers);

		const now = new Date();
		const defaultStart = new Date(now.getFullYear(), now.getMonth() - 3, 1);
		const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 3, 1);
		const startStr = defaultStart.toISOString().slice(0, 10);
		const endStr = defaultEnd.toISOString().slice(0, 10);

		setStartDate(startStr);
		setEndDate(endStr);

		setLoading(true);
		apiGetWorkerBoardingsBriefSummary({
			start_date: startStr,
			end_date: endStr,
		})
			.then(data => setSummary(data))
			.catch(error => {
				if (axios.isAxiosError(error) && error.response?.status === 404) {
					toast.warning('Nenhum dado encontrado para o período inicial.');
					handleClear();
				} else {
					toast.error('Falha ao obter os dados iniciais.');
				}
			})
			.finally(() => setLoading(false));
	}, []);

	// filtragem via botão
	const handleFilter = async () => {
		setLoading(true);
		try {
			const data = await apiGetWorkerBoardingsBriefSummary({
				start_date: startDate || undefined,
				end_date: endDate || undefined,
				worker_ids: selectedWorkerIds,
				project_numbers: selectedWOs,
			});
			setSummary(data);
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				toast.warning('Nenhum dado encontrado para os filtros informados.');
				// limpa apenas os dados dos gráficos
				setSummary({
					items: [],
					null_worker_percentage: 100,
					monthly_counts: [],
				});
			} else {
				toast.error('Falha ao obter os dados.');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleClear = () => {
		setStartDate('');
		setEndDate('');
		setSelectedWOs([]);
		setSelectedWorkerIds([]);
		setSummary(null);
	};

	const handleExport = () => {
		if (!summary) return;

		// 1) Renomeia as colunas mapeando o array
		const itemsRenomeados = summary.items.map(item => ({
			WO: item.project_number,
			Cliente: item.client_name,
			Plataforma: item.platform_name,
			'Nº Embarque': item.boarding_number,
			'Data do embarque (programação)': item.scheduled_embarkation_date,
			'Data do desembarque (programação)': item.scheduled_disembarkation_date,
			Trabalhador: item.worker_name,
			'Embarque programado': item.scheduled_start_date,
			'Desembarque programado': item.scheduled_end_date,
		}));

		const ws = XLSX.utils.json_to_sheet(itemsRenomeados);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Items');

		// Mesmos passos para monthly_counts, se quiser renomear:
		const monthsRenomeados = summary.monthly_counts.map(m => ({
			Mês: m.month,
			'Vagas Sem Programação': m.null_count,
			'Vagas Programadas': m.nonnull_count,
		}));
		const ws2 = XLSX.utils.json_to_sheet(monthsRenomeados);
		XLSX.utils.book_append_sheet(wb, ws2, 'MonthlyCounts');

		XLSX.writeFile(wb, 'WorkerBoardingsSummary.xlsx');
	};

	// chart data
	const pieData = summary
		? [
				{ name: 'sem programação', value: summary.null_worker_percentage },
				{ name: 'programados', value: 100 - summary.null_worker_percentage },
		  ]
		: [];

	const barData = summary
		? summary.monthly_counts.map(row => ({
				month: row.month,
				null: row.null_count,
				nonnull: row.nonnull_count,
		  }))
		: [];

	return (
		<Grid item xs={12}>
			<Paper elevation={5} style={{ padding: 20 }}>
				<Box mt={0} p={2} sx={{ mb: 4 }} component="section">
					{/* <Typography variant="h6" gutterBottom>
						Brief Summary
					</Typography> */}
					<Typography
						variant="h5"
						component="h2"
						style={{ marginBottom: '40px' }}
					>
						Dados dos colaboradores programados
					</Typography>
					<Grid container spacing={2} alignItems="center">
						<Grid item>
							<TextField
								label="Data início"
								type="date"
								value={startDate}
								onChange={e => setStartDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item>
							<TextField
								label="Data fim"
								type="date"
								value={endDate}
								onChange={e => setEndDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item>
							<FormControl sx={{ minWidth: 200 }}>
								<InputLabel>WO</InputLabel>
								<Select
									multiple
									value={selectedWOs}
									onChange={(e: SelectChangeEvent<number[]>) => {
										const v =
											typeof e.target.value === 'string'
												? (e.target.value as string).split(',').map(Number)
												: e.target.value;
										setSelectedWOs(v);
									}}
									renderValue={v => (v as number[]).join(', ')}
									label="WO"
								>
									{[...dropdownWOs]
										.sort((a, b) => a.project_number - b.project_number)
										.map(wo => (
											<MenuItem key={wo.id} value={wo.project_number}>
												{wo.project_number}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item>
							<FormControl sx={{ minWidth: 200 }}>
								<InputLabel>Trabalhadores</InputLabel>
								<Select
									multiple
									value={selectedWorkerIds}
									onChange={(e: SelectChangeEvent<number[]>) => {
										const v =
											typeof e.target.value === 'string'
												? e.target.value.split(',').map(Number)
												: e.target.value;
										setSelectedWorkerIds(v);
									}}
									renderValue={v =>
										(v as number[])
											.map(
												id =>
													dropdownWorkers.find(w => w.id === id)?.worker_name
											)
											.join(', ')
									}
									label="Trabalhadores"
								>
									{[...dropdownWorkers]
										.sort((a, b) => a.worker_name.localeCompare(b.worker_name))
										.map(w => (
											<MenuItem key={w.id} value={w.id}>
												{w.worker_name}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item>
							<Button
								variant="contained"
								onClick={handleFilter}
								disabled={loading}
							>
								{loading ? <CircularProgress size={24} /> : 'Filtrar'}
							</Button>
						</Grid>
						<Grid item>
							<Button
								variant="outlined"
								onClick={handleClear}
								disabled={loading}
							>
								Limpar
							</Button>
						</Grid>
						{/* <Grid item>
							<Button
								variant="contained"
								onClick={handleExport}
								disabled={!summary || loading}
							>
								Exportar Excel
							</Button>
						</Grid> */}
						<Grid item>
							<Tooltip
								title="Exportar relatório"
								componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}
							>
								<IconButton
									onClick={handleExport}
									disabled={!summary || loading}
									sx={{
										backgroundColor: '#007eb8',
										color: 'white',
										width: 30,
										height: 30,
										borderRadius: '4px',
										'&:hover': { backgroundColor: '#006a9e' },
									}}
								>
									<Download fontSize="small" />
								</IconButton>
							</Tooltip>
						</Grid>
					</Grid>

					{summary && (
						<Grid container spacing={4} mt={2}>
							<Grid item xs={12} md={6} height={300}>
								<Typography
									variant="h6"
									align="center"
									// style={{ marginBottom: 16, fontWeight: 'bold' }}
								>
									Total de vagas com programação
								</Typography>
								<ResponsiveContainer>
									<PieChart
										margin={{ top: 10 }}
										// margin={{ top: 30, right: 20, bottom: 20, left: 20 }}
									>
										<Pie
											data={pieData}
											dataKey="value"
											nameKey="name"
											outerRadius={100}
											label={({ value }) => `${value.toFixed(1)}%`}
											isAnimationActive={true}
										>
											{pieData.map((entry, index) => (
												<Cell
													key={entry.name}
													fill={COLORS[index % COLORS.length]}
												/>
											))}
										</Pie>
										<Legend />
										<RechartsTooltip />
									</PieChart>
								</ResponsiveContainer>
							</Grid>
							<Grid item xs={12} md={6} height={300}>
								<ResponsiveContainer>
									<BarChart
										data={barData}
										margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
									>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="month" />
										<YAxis />
										<RechartsTooltip />
										<Legend />
										{/* <Bar dataKey="null" stackId="a" />
										<Bar dataKey="nonnull" stackId="a" /> */}
										<Bar
											dataKey="null"
											stackId="a"
											name="Não programados"
											fill="#FF0000"
											isAnimationActive={true}
										/>
										<Bar
											dataKey="nonnull"
											stackId="a"
											name="Programados"
											fill="#0088FE"
											isAnimationActive={true}
										/>
									</BarChart>
								</ResponsiveContainer>
							</Grid>
						</Grid>
					)}
				</Box>
			</Paper>
		</Grid>
	);
}
